import React from 'react'
import { Select, Spin } from '@surecompanies/core_components'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import type { AvailableVcnCard } from './VcnPaymentManagement.types'

interface VcnCardSelectProps {
  value: string | undefined
  onChange: (value: string | undefined) => void
  availableCards: AvailableVcnCard[]
  virtualCardKey: string | undefined
  currentHealthPlanId: string | undefined
  loading?: boolean
  disabled?: boolean
}

const COLUMN_STYLES = {
  vcn: { width: 160, padding: '0 8px' },
  hios: { width: 60, padding: '0 8px' },
  member: { width: 120, padding: '0 8px' },
  carrier: { width: 100, padding: '0 8px' },
  status: { width: 100, padding: '0 8px' },
  provider: { width: 100, padding: '0 8px' },
} as const

const HEADER_STYLE = {
  fontWeight: 600,
  fontSize: 12,
  color: '#8c8c8c',
} as const

function DropdownHeader() {
  return (
    <div
      style={{
        display: 'flex',
        marginBottom: 4,
        borderBottom: '1px solid #e8e8e8',
        paddingBottom: 4,
      }}
    >
      <span style={{ ...COLUMN_STYLES.vcn, ...HEADER_STYLE }}>VCN</span>
      <span style={{ ...COLUMN_STYLES.hios, ...HEADER_STYLE }}>HIOS</span>
      <span style={{ ...COLUMN_STYLES.member, ...HEADER_STYLE }}>Member</span>
      <span style={{ ...COLUMN_STYLES.carrier, ...HEADER_STYLE }}>
        Current Carrier
      </span>
      <span style={{ ...COLUMN_STYLES.status, ...HEADER_STYLE }}>
        VCN Status
      </span>
      <span style={{ ...COLUMN_STYLES.provider, ...HEADER_STYLE }}>
        VCN Provider
      </span>
    </div>
  )
}

interface CardOptionProps {
  card: AvailableVcnCard
  isActive: boolean
  currentHealthPlanId: string | undefined
}

function CardOption({ card, isActive, currentHealthPlanId }: CardOptionProps) {
  const textColor = isActive ? '#1890ff' : '#595959'
  const cellStyle = {
    padding: '4px 8px',
    borderRight: '1px solid #f0f0f0',
    color: textColor,
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        backgroundColor: isActive ? '#e6f7ff' : undefined,
      }}
    >
      <span
        style={{
          ...cellStyle,
          width: COLUMN_STYLES.vcn.width,
          fontWeight: isActive ? 600 : 400,
        }}
      >
        {card.card}
      </span>
      <span style={{ ...cellStyle, width: COLUMN_STYLES.hios.width }}>
        {card.HIOS?.slice(-5) || '-'}
      </span>
      <span style={{ ...cellStyle, width: COLUMN_STYLES.member.width }}>
        {card.name}
      </span>
      <span style={{ ...cellStyle, width: COLUMN_STYLES.carrier.width }}>
        {card.HIOS === currentHealthPlanId ? 'YES' : 'NO'}
      </span>
      <span style={{ ...cellStyle, width: COLUMN_STYLES.status.width }}>
        {card.status}
      </span>
      <span
        style={{
          width: COLUMN_STYLES.provider.width,
          padding: '4px 8px',
          color: textColor,
        }}
      >
        {card.virtualCardType}
      </span>
    </div>
  )
}

export function VcnCardSelect({
  value,
  onChange,
  availableCards,
  virtualCardKey,
  currentHealthPlanId,
  loading = false,
  disabled = false,
}: VcnCardSelectProps) {
  const isCardActive = (card: AvailableVcnCard): boolean => {
    if (!virtualCardKey) return false
    return card.virtualCardType === VirtualCardType.MARQETA
      ? card.id === virtualCardKey
      : card.merchantLogId === virtualCardKey
  }

  return (
    <Select
      placeholder="Select"
      value={value}
      onChange={onChange}
      style={{ width: '100%' }}
      loading={loading}
      disabled={disabled || loading}
      optionLabelProp="label"
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 800 }}
      allowClear
      notFoundContent={
        loading ? <Spin size="small" /> : 'No VCN cards available'
      }
      dropdownRender={(menu) => (
        <div style={{ padding: '4px 4px 12px' }}>
          <DropdownHeader />
          {menu}
        </div>
      )}
    >
      {availableCards.map((card, index) => {
        const isActive = isCardActive(card)
        return (
          <Select.Option
            key={index}
            value={String(index)}
            label={card.card}
            disabled={isActive}
            style={{ margin: 0, padding: 0, minHeight: 'unset' }}
          >
            <CardOption
              card={card}
              isActive={isActive}
              currentHealthPlanId={currentHealthPlanId}
            />
          </Select.Option>
        )
      })}
    </Select>
  )
}
